import { useConvertCurrency } from '@utils/currencyConverter';
import { getCountryByCurrency } from '@utils/locations';

import { COUNTRIES } from '../../constants/locations';
import { useCountryCode } from '../../hooks/useCountryCode';
import { useTaxPricing } from '../../hooks/useTaxPricing';
import { CountryCode, CurrencyCode } from '../../types/Locations';
import { formatPrice } from '../../utils/prices';

interface Props {
  className?: string;
  price: number;
  skipTaxCalculation?: boolean;
  countryCode?: CountryCode;
  skipConvertCurrency?: boolean;
  prefix?: string;
  inMinorUnit?: boolean;
  suffix?: string;
  currencyCode?: CurrencyCode;
}

export const ProductPrice = ({
  className,
  countryCode,
  currencyCode,
  inMinorUnit = false,
  prefix,
  price,
  skipConvertCurrency,
  skipTaxCalculation,
  suffix,
}: Props) => {
  const [userPreferencesCountryCode] = useCountryCode();
  const { calculateTaxPricing } = useTaxPricing({
    countryCode: countryCode || userPreferencesCountryCode,
  });

  const countryByCurrency = currencyCode
    ? getCountryByCurrency(currencyCode)
    : null;
  const { currency, minorUnit, roundingFn } =
    COUNTRIES[countryByCurrency || countryCode || userPreferencesCountryCode];
  const { convert } = useConvertCurrency();

  const priceMinor = inMinorUnit ? price / minorUnit : price;

  const priceInSelectedCurrency = !skipConvertCurrency
    ? convert(priceMinor, currency)
    : priceMinor;

  const priceRounded = roundingFn
    ? roundingFn(priceInSelectedCurrency)
    : Math.round(priceInSelectedCurrency);

  const priceWithTaxes = !skipTaxCalculation
    ? calculateTaxPricing(priceRounded)
    : priceRounded;

  const pricing = formatPrice(priceWithTaxes, currency);

  return (
    <span className={className}>
      {prefix ? `${prefix} ${pricing}` : pricing}
      {suffix && ` ${suffix}`}
    </span>
  );
};
